export enum AuthorRole {
  Customer = 'customer',
  Agent = 'agent',
}

export enum TraceActionClient {
  AllSpeakTranslationClient = 'AllSpeakTranslationClient',
  IntentDetectionClient = 'IntentDetectionClient',
  IntentInfoClient = 'IntentInfoClient',
  EscalationChecker = 'EscalationChecker',
  PtolemyFaqByKeyClient = 'PtolemyFaqByKeyClient',
  PtolemySearchClient = 'PtolemySearchClient',
  GusteauClient = 'GusteauClient',
  LedgerTransactionsClient = 'LedgerTransactionsClient',
  OpenAIPromptGenerator = 'OpenAIPromptGenerator',
  OpenAIChat = 'OpenAIChat',
  OpenAIModerator = 'OpenAIModerator',
  ModerationClient = 'ModerationClient',
}

export enum SupportTextGenerationStatusOutState {
  Pending = 'PENDING',
  InProgress = 'IN_PROGRESS',
  Generated = 'GENERATED',
  Failed = 'FAILED',
  Deleted = 'DELETED',
}

export enum SupportTextGenerationResultState {
  Failed = 'FAILED',
  Generated = 'GENERATED',
}

export enum CreateFixedPromptTextGenerationOutState {
  Created = 'CREATED',
  Deleted = 'DELETED',
}

export enum GetFixedPromptTextGenerationOutState {
  Pending = 'PENDING',
  InProgress = 'IN_PROGRESS',
  Generated = 'GENERATED',
  Failed = 'FAILED',
  Deleted = 'DELETED',
  Empty = 'EMPTY',
}

export type UserBase = {
  email: string
  firstName: string
  lastName: string
  username: string
  id: string
}

export type UserOut = UserBase & {
  permissions: string[]
  roles: []
}

export type Message = {
  messageId: string
  created: string
  authorRole: AuthorRole
  authorName?: string | null
  text: string
}

export type DialogueSnapshot = {
  dialogueId: string
  correlationId: string
  messages: Message[]
}

export type GetDialogueSnapshotResponse = {
  dialogueSnapshots: DialogueSnapshot[]
}

export type OpenAIChatActionModel = {
  name: string
  parameters: {
    [key: string]: unknown
  }
  endpointId: string | null
  location: string | null
  maxRetries: number
  readTimeout: string
  tools: unknown
  vendor: string
}

export type OpenAIChatAction = {
  id: string
  name: string
  humanReadableInput?: Record<string, string> | null
  humanReadableOutput?: Record<string, string> | null
  client: TraceActionClient.OpenAIChat
  clientInput: {
    model: OpenAIChatActionModel
    promptRevision: string
    messages: { content: string; role: string }[]
  }
  clientOutput: Record<string, unknown>
  duration: number
}

export type Transaction = {
  id: string
  state: string
  type: string
  createdDate: string
  completedDate: string
  description: string
  billAmount?: {
    amount: number
    currency: string
  } | null
  transactionAmount?: {
    amount: number
    currency: string
  } | null
  fee?: {
    amount: number
    currency: string
  } | null
  billPocket?: {
    balance?: {
      amount: number
      currency: string
    }
  }
}

export type LedgerTransactionsAction = {
  id: string
  name: string
  humanReadableInput?: Record<string, string> | null
  humanReadableOutput?: Record<string, string> | null
  client: TraceActionClient.LedgerTransactionsClient
  clientInput: {
    clientId: string
    [key: string]: unknown
  }
  clientOutput: {
    transactions?: {
      data: Transaction
    }[]
  }
  duration: number
}

export type PtolemySearchAction = {
  id: string
  name: string
  humanReadableInput?: Record<string, string> | null
  humanReadableOutput?: Record<string, string> | null
  client: TraceActionClient.PtolemySearchClient
  clientInput: Record<string, unknown>
  clientOutput: {
    data: {
      content: string
      score: number
      externalReference?: { source: string; value: string }
    }[]
  }
  duration: number
}

export type KnownClientAction = {
  id: string
  name: string
  humanReadableInput?: Record<string, string> | null
  humanReadableOutput?: Record<string, string> | null
  client:
    | TraceActionClient.AllSpeakTranslationClient
    | TraceActionClient.IntentDetectionClient
    | TraceActionClient.IntentInfoClient
    | TraceActionClient.EscalationChecker
    | TraceActionClient.PtolemyFaqByKeyClient
    | TraceActionClient.GusteauClient
    | TraceActionClient.OpenAIPromptGenerator
    | TraceActionClient.OpenAIModerator
    | TraceActionClient.ModerationClient
  clientInput: Record<string, unknown>
  clientOutput: Record<string, unknown>
  duration: number
}

export type FixedPromptTextGenerationSuccess = {
  state: SupportTextGenerationResultState.Generated
  response: {
    author: string
    text: string
  }
}

export type FixedPromptTextGenerationError = {
  state: SupportTextGenerationResultState.Failed
  code: number
  message: string
}

export type SupportTextGenerationStatusOut = {
  id: string
  dialogueSnapshotId: string
  correlationId: string
  state: SupportTextGenerationStatusOutState
  created: string
  updated: string
  result: FixedPromptTextGenerationSuccess | FixedPromptTextGenerationError
}

export type Trace =
  | OpenAIChatAction
  | LedgerTransactionsAction
  | PtolemySearchAction
  | KnownClientAction

export type AiReply = {
  text: string
  author: string
  timestamp: string
}

export type AiDebugProcess = {
  id: string
  correlationId: string
  label: string
  processName: string
  traces: Trace[]
  reply?: AiReply
}

export type AiDebugResponse = {
  debug: AiDebugProcess[]
}

export type CreateFixedPromptTextGenerationOut = {
  fixedPromptTextGenerationId: string
  state: CreateFixedPromptTextGenerationOutState
}

export type GetFixedPromptTextGenerationOut = {
  id: string
  correlationId: string
  supportTextGenerationId: string
  created: string
  updated: string
} & (
  | {
      state: GetFixedPromptTextGenerationOutState.Generated
      result: FixedPromptTextGenerationSuccess
    }
  | {
      state: GetFixedPromptTextGenerationOutState.Failed
      result: FixedPromptTextGenerationError
    }
  | {
      state:
        | GetFixedPromptTextGenerationOutState.Pending
        | GetFixedPromptTextGenerationOutState.InProgress
        | GetFixedPromptTextGenerationOutState.Deleted
    }
)

export type AssistantConversationOut = {
  id: string
  dialogueSnapshotId: string
  correlationId: string
  created: string
  updated: string
} & (
  | {
      state: GetFixedPromptTextGenerationOutState.Generated
      result: FixedPromptTextGenerationSuccess
    }
  | {
      state: GetFixedPromptTextGenerationOutState.Failed
      result: FixedPromptTextGenerationError
    }
  | {
      state:
        | GetFixedPromptTextGenerationOutState.Pending
        | GetFixedPromptTextGenerationOutState.InProgress
        | GetFixedPromptTextGenerationOutState.Deleted
    }
)

export type AssistantNoConversationOut = {
  correlationId: null
  dialogueSnapshotId: null
  state: GetFixedPromptTextGenerationOutState.Empty
  result: null
}

export type TextPayloadModel = {
  type: 'text'
  value: string
}

export type MessagePayloadModel = TextPayloadModel

export type MessageModel = {
  messageId: string
  authorRole: AuthorRole
  payload: MessagePayloadModel
  created: string
  authorName?: string
}

export type GenerateAssistantChatMessagePayload = {
  previousDialogueSnapshotId: string | null
  correlationId: string
  messages: MessageModel[]
}

export type PlaygroundUsersOut = {
  id: string
  firstName: string
  lastName: string
}

export type AssistantDialoguesOut = {
  assistantDialogues: AssistantDialogueEntyOut[]
}

export type AssistantDialogueEntyOut = {
  ownerId: string
  correlationId: string
  lastInteraction: string
  messageCount: number
}

export type AssistantDialogueEnty = {
  owner: PlaygroundUsersOut | undefined
  correlationId: string
  lastInteraction: string
  messageCount: number
}

export enum AssistantChatMessageRating {
  ThumbUp = 'THUMB_UP',
  ThumbDown = 'THUMB_DOWN',
}

export type CreateAssistantChatMessageFeedbackPayload = {
  assistantGenerationId: string
  rating: AssistantChatMessageRating
  feedback: string | null
  reasons: string[] | null
}

export type AssistantChatMessageFeedback = {
  feedbackId: string
  assistantGenerationId: string
  rating: AssistantChatMessageRating
  feedback: string | null
  reasons: string[] | null
  createdDate: string
  updatedDate: string
  author: string
}

export type AssistantChatMessageFeedbackPayload = {
  feedbacks: AssistantChatMessageFeedback[]
}
